import { useCallback, useMemo, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Banknote,
  CreditCard,
  QrCode,
  Wallet,
  Loader2,
  RefreshCw,
  Lock,
  Unlock,
  Undo2,
  Hourglass,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ShiftSummaryCard } from '@/components/pos/ShiftSummaryCard';
import { CashCountPanel } from '@/components/pos/CashCountPanel';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { EmptyState } from '@/components/pos/EmptyState';
import {
  useShiftSummary,
  useCurrentShift,
  useOpenShift,
  useCloseShift,
} from '@/hooks/queries';
import { todayISO, formatDate, formatTime, rupiah, number } from '@/lib/format';
import { shiftTenderRows, shiftTenderTotals } from '@/lib/shiftTenders';
import type { ShiftAdjustments } from '@/types/sale';

/** Tile icon per tender. An unmapped tender still renders — with a wallet. */
const TENDER_ICON: Record<string, LucideIcon> = {
  cash: Banknote,
  'e-money': CreditCard,
  midtrans: QrCode,
  'edc-permata': CreditCard,
};

/** The backend caps the close note at 500 chars (`ShiftService.closeShift`). */
const NOTE_MAX = 500;

export function ShiftSummaryPage() {
  const today = todayISO();
  const { data, isLoading, isFetching, isError, refetch } = useShiftSummary(today);
  const { data: current, isLoading: shiftLoading } = useCurrentShift();
  const openMut = useOpenShift();
  const closeMut = useCloseShift();

  const [floatInput, setFloatInput] = useState('');
  // The counted drawer lives in STATE, not only in a ref, because the variance
  // it implies decides whether the reason field is on screen. The denominations
  // stay in a ref: nothing renders off them, and a fresh object every keystroke
  // would feed the identity loop described on `handleCount` below.
  const [countedCash, setCountedCash] = useState(0);
  const denomsRef = useRef<Record<string, number>>({});
  const [note, setNote] = useState('');

  // Every tender the API returned — not a hardcoded three. See
  // `shiftTenderRows` for what hiding one costs a cashier.
  const tenders = useMemo(() => shiftTenderRows(data?.byMethod), [data?.byMethod]);
  const { gross: grandGross, count: grandCount } = useMemo(
    () => shiftTenderTotals(tenders),
    [tenders],
  );

  const cashGross = tenders.find((t) => t.key === 'cash')?.gross ?? 0;
  // While a shift is open, the drawer should also hold the opening float.
  const openingFloat = current?.openingFloat ?? 0;
  const expectedCash = openingFloat + cashGross;
  const variance = countedCash - expectedCash;
  // `CashCountPanel` shows the variance as "—" until at least one note or coin
  // is entered, so the reason field holds to the same threshold — asking why a
  // drawer is short before anyone has counted it would contradict the panel
  // directly above it. A positive count is exactly "counting started": every
  // denomination is positive, so any entry makes the sum positive.
  const counting = countedCash > 0;
  const needsReason = counting && variance !== 0;

  // `useCallback` with no deps is load-bearing, not tidiness: `CashCountPanel`
  // lists `onChange` in the effect deps that emits the count, so a new closure
  // each render would re-emit, re-set state, re-render — forever. Stable
  // identity plus a number-valued setState lets React bail out instead.
  const handleCount = useCallback((counted: number, denoms: Record<string, number>) => {
    denomsRef.current = denoms;
    setCountedCash(counted);
  }, []);

  const handleOpen = () => {
    openMut.mutate(Math.max(0, Number(floatInput) || 0), {
      onSuccess: () => {
        toast.success('Shift dibuka.');
        setFloatInput('');
      },
      onError: (e: { message?: string }) => toast.error(e?.message ?? 'Gagal membuka shift.'),
    });
  };

  const handleClose = () => {
    const trimmed = note.trim();
    closeMut.mutate(
      {
        countedCash,
        denominations: denomsRef.current,
        // Only a real discrepancy carries a reason. A note typed before the
        // count came back into balance would otherwise be filed against a
        // variance of zero, which reads as a discrepancy that never happened.
        note: needsReason && trimmed ? trimmed : null,
      },
      {
        onSuccess: (shift) => {
          const v = shift.variance ?? 0;
          const detail =
            v === 0 ? 'Kas sesuai.' : v > 0 ? `Lebih ${rupiah(v)}.` : `Kurang ${rupiah(Math.abs(v))}.`;
          toast.success(`Shift ditutup. ${detail}`);
          setNote('');
        },
        onError: (e: { message?: string }) => toast.error(e?.message ?? 'Gagal menutup shift.'),
      },
    );
  };

  return (
    <div className="mx-auto h-full max-w-4xl overflow-y-auto px-6 py-8 pos-scroll">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink-900">Ringkasan Shift</h1>
          <p className="text-sm text-muted-foreground">{formatDate(today)}</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Segarkan
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20 text-ink-400">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : !data ? (
        <EmptyState
          icon={Wallet}
          title="Ringkasan belum tersedia"
          description={
            isError
              ? 'Gagal memuat ringkasan shift. Coba segarkan.'
              : 'Endpoint ringkasan shift belum aktif. Hubungi administrator.'
          }
          action={
            <Button variant="outline" onClick={() => refetch()}>
              Coba lagi
            </Button>
          }
        />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {tenders.map((t) => (
              <ShiftSummaryCard
                key={t.key}
                label={t.label}
                icon={TENDER_ICON[t.key] ?? Wallet}
                count={t.count}
                gross={t.gross}
                discount={t.discount}
                accent={t.key === 'cash' ? 'crimson' : 'neutral'}
              />
            ))}
          </div>

          <Card>
            <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Total bruto hari ini</p>
                <MoneyValue value={grandGross} emphasis className="text-4xl" />
                <p className="tnum mt-1 text-sm text-ink-400">{grandCount} transaksi</p>
              </div>
              <div className="rounded-xl border-2 border-gold-500/40 bg-gold-200/40 px-6 py-4 text-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">
                  Kas tunai diharapkan
                </p>
                <MoneyValue value={expectedCash} emphasis className="text-2xl" />
                <p className="mt-1 text-xs text-ink-400">
                  {openingFloat > 0 ? `Modal ${rupiah(openingFloat)} + tunai` : 'Untuk rekonsiliasi laci kas'}
                </p>
              </div>
            </CardContent>
          </Card>

          {data.adjustments && <AdjustmentsCard adj={data.adjustments} />}

          {/* Till session — open, or count + close */}
          {shiftLoading ? (
            <div className="flex justify-center py-4 text-ink-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : !current ? (
            <Card>
              <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0">
                  <p className="font-display text-base font-semibold text-ink-900">Belum ada shift berjalan</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    Buka shift untuk mulai merekonsiliasi laci kas.
                  </p>
                </div>
                <div className="flex items-end gap-2">
                  <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-ink-400">
                    Modal awal
                    <div className="flex items-center rounded-lg border border-cream-200 bg-white">
                      <span className="pl-2.5 text-sm text-ink-400">Rp</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        value={floatInput}
                        onChange={(e) => setFloatInput(e.target.value)}
                        placeholder="0"
                        className="tnum h-9 w-28 bg-transparent px-2 text-sm text-ink-900 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  </label>
                  <Button className="gap-2" onClick={handleOpen} disabled={openMut.isPending}>
                    {openMut.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Unlock className="h-4 w-4" />
                    )}
                    Buka shift
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-500/25 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
                <span className="flex items-center gap-2 font-medium">
                  <Unlock className="h-4 w-4" /> Shift berjalan sejak {formatTime(current.openedAt)}
                </span>
                {openingFloat > 0 && (
                  <span className="tnum text-emerald-700">Modal awal {rupiah(openingFloat)}</span>
                )}
              </div>

              <CashCountPanel expectedCash={expectedCash} onChange={handleCount} />

              {/* A reason for the discrepancy — the backend has always accepted
                  `note` and stored it on the closed shift, but the POS never
                  sent one, so a variance was recorded with no explanation
                  anywhere (#1749). Shown only when the drawer is actually off:
                  a shift that balances gains no extra field to fill in. */}
              {needsReason && (
                <div className="rounded-2xl border border-gold-500/40 bg-gold-200/30 px-5 py-4">
                  <label
                    htmlFor="shift-variance-note"
                    className="flex flex-wrap items-baseline gap-x-2 text-xs font-semibold uppercase tracking-wide text-ink-600"
                  >
                    Alasan selisih
                    <span className="tnum font-normal normal-case text-ink-400">
                      {variance > 0 ? 'Lebih' : 'Kurang'} {rupiah(Math.abs(variance))}
                    </span>
                  </label>
                  <textarea
                    id="shift-variance-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
                    maxLength={NOTE_MAX}
                    rows={2}
                    placeholder="Mis. kembalian kurang bayar, uang palsu ditolak, selisih pecahan koin…"
                    className="mt-2 w-full resize-none rounded-lg border border-cream-200 bg-white px-3 py-2 text-sm text-ink-900 outline-none placeholder:text-ink-400/70 focus:border-gold-500"
                  />
                  <p className="mt-1.5 text-xs text-ink-400">
                    Tercatat pada shift yang ditutup. Kosongkan jika penyebabnya belum diketahui.
                  </p>
                </div>
              )}

              <Button
                className="w-full gap-2"
                onClick={handleClose}
                disabled={closeMut.isPending}
              >
                {closeMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                Tutup shift
              </Button>
            </>
          )}

          <Button variant="outline" className="w-full gap-2" onClick={() => window.print()}>
            Cetak Laporan Shift
          </Button>
        </div>
      )}
    </div>
  );
}

/** Void / refund recap for the day (F.01) — rendered only when the BE sends it. */
function AdjustmentsCard({ adj }: { adj: ShiftAdjustments }) {
  const voidCount = Number(adj.voidCount) || 0;
  const voidAmount = Number(adj.voidAmount) || 0;
  const refundCount = Number(adj.refundCount) || 0;
  const refundAmount = Number(adj.refundAmount) || 0;
  const pending = Number(adj.pendingRequests) || 0;
  return (
    <Card data-testid="shift-adjustments">
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-center gap-2">
          <Undo2 className="h-5 w-5 text-primary" />
          <p className="font-display text-base font-semibold text-ink-900">Void &amp; Refund</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <AdjStat label="Void" count={voidCount} amount={voidAmount} />
          <AdjStat label="Refund" count={refundCount} amount={refundAmount} />
          <div className="rounded-lg border border-cream-200 bg-white px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Permintaan menunggu</p>
            <p className="tnum font-display text-2xl font-bold text-ink-900">{number(pending)}</p>
          </div>
        </div>
        {pending > 0 && (
          <div className="flex items-start gap-2 rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <Hourglass className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              Masih ada {number(pending)} permintaan void/refund menunggu persetujuan. Shift tetap bisa
              ditutup — laporkan ke supervisor.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AdjStat({ label, count, amount }: { label: string; count: number; amount: number }) {
  return (
    <div className="rounded-lg border border-cream-200 bg-white px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">{label}</p>
      <MoneyValue value={amount} emphasis className="text-xl" />
      <p className="tnum text-xs text-ink-400">{number(count)} transaksi</p>
    </div>
  );
}
